/**
 * 중증도분류 수행결과 — `contentCode`/행 데이터에 따라 Mass · KTAS · Radiation 테이블로 분기.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MassTriageResultTable } from './MassTriageResult';
import { KtasTriageResultTable } from './KtasTriageResult';
import { RadiationTriageResultTable } from './RadiationTriageResult';
import type { TriagePatientRow, TriageViewMode } from './triageShared';
import { resolveTriageSessionVariant } from './triageShared';
import type { WinnerEntry } from './Top3Winners';

export type { TriagePatientRow, TriageViewMode };
export { resolveTriageSessionVariant } from './triageShared';

export interface TriageResultTableProps {
    title: string;
    patients: TriagePatientRow[];
    viewMode?: TriageViewMode;
    onViewModeChange?: (mode: TriageViewMode) => void;
    isAllParticipantsView?: boolean;
    winners?: WinnerEntry[];
    contentCode?: string | null;
    /** false 이면 확대뷰 토글 숨김 (현재 KTAS 에서만 사용) */
    canShowEnlargedView?: boolean;
}

/** 기존 단일 진입점 — 내부에서 Mass / KTAS / Radiation 구현으로 분기 */
export const TriageResultTable: React.FC<TriageResultTableProps> = ({ contentCode, canShowEnlargedView, ...rest }) => {
    const variant = resolveTriageSessionVariant(contentCode, rest.patients);
    if (variant === 'radiation') {
        return <RadiationTriageResultTable {...rest} />;
    }
    if (variant === 'ktas') {
        return <KtasTriageResultTable {...rest} canShowEnlargedView={canShowEnlargedView} />;
    }
    return <MassTriageResultTable {...rest} />;
};

export { MassTriageResultTable } from './MassTriageResult';
export { KtasTriageResultTable } from './KtasTriageResult';
export { RadiationTriageResultTable } from './RadiationTriageResult';

/** 클래스 시뮬레이션 세션 수행결과 래퍼 (뷰 모드 상태 + 제목) */
export function TriageSessionPerformanceSection({
    patients,
    isAllParticipantsView,
    winners,
    contentCode,
    canShowEnlargedView,
}: {
    patients?: TriagePatientRow[];
    isAllParticipantsView?: boolean;
    winners?: WinnerEntry[];
    contentCode?: string | null;
    canShowEnlargedView?: boolean;
}): React.ReactElement | null {
    const { t } = useTranslation('class');
    const [viewMode, setViewMode] = useState<TriageViewMode>('card');
    return (
        <div className="space-y-6 !mt-6 !mb-2">
            <TriageResultTable
                title={t('simulationResults.tablePerformance')}
                patients={patients ?? []}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isAllParticipantsView={isAllParticipantsView}
                winners={winners}
                contentCode={contentCode}
                canShowEnlargedView={canShowEnlargedView}
            />
        </div>
    );
}
